import { useEffect, useState, useCallback } from 'react';

interface GitHubAuthConfig {
  clientId: string;
  redirectUri?: string;
  scopes?: string[];
  onSuccess: (response: any) => void;
  onError: (error: any) => void;
}

interface GitHubAuthHook {
  isLoaded: boolean;
  signIn: () => void;
  error: string | null;
}

const useGitHubAuth = ({
  clientId,
  redirectUri = `${window.location.origin}/github-callback.html`,
  scopes = ['user:email'],
  onSuccess,
  onError,
}: GitHubAuthConfig): GitHubAuthHook => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!clientId || clientId === 'your-github-client-id') {
      setError('GitHub Client ID not configured. Please set REACT_APP_GITHUB_CLIENT_ID in your environment variables.');
      return;
    }

    // GitHub OAuth doesn't require SDK initialization
    setIsLoaded(true);
    setError(null);
  }, [clientId]);

  const signIn = useCallback(() => {
    if (!isLoaded) {
      const error = new Error('GitHub auth not ready');
      setError('GitHub authentication not ready');
      onError(error);
      return;
    }

    try {
      // Generate random state for CSRF protection
      const state = Math.random().toString(36).substring(7);
      sessionStorage.setItem('github_auth_state', state);

      // Build GitHub OAuth URL
      const scope = scopes.join(' ');
      const authUrl = `https://github.com/login/oauth/authorize?` +
        `client_id=${encodeURIComponent(clientId)}` +
        `&redirect_uri=${encodeURIComponent(redirectUri)}` +
        `&scope=${encodeURIComponent(scope)}` +
        `&state=${encodeURIComponent(state)}`;

      // Open GitHub OAuth in popup window
      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      const popup = window.open(
        authUrl,
        'GitHub Sign In',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      if (!popup) {
        throw new Error('Popup blocked. Please allow popups for this site.');
      }

      // Listen for OAuth callback
      const handleMessage = async (event: MessageEvent) => {
        // Verify origin for security
        if (event.origin !== window.location.origin) {
          return;
        }

        if (event.data.type === 'github-oauth-success') {
          window.removeEventListener('message', handleMessage);

          const { code, state: returnedState } = event.data;
          const savedState = sessionStorage.getItem('github_auth_state');
          sessionStorage.removeItem('github_auth_state');

          // Verify state to prevent CSRF
          if (returnedState !== savedState) {
            const error = new Error('State mismatch - possible CSRF attack');
            setError('Authentication failed: security check failed');
            onError(error);
            return;
          }

          try {
            // Send code to backend for server-side token exchange
            // Backend will handle the exchange securely using client_secret
            onSuccess({
              code: code,
              state: returnedState,
            });

          } catch (err: any) {
            console.error('GitHub authentication error:', err);
            setError(err.message || 'GitHub authentication failed');
            onError(err);
          }
        } else if (event.data.type === 'github-oauth-error') {
          window.removeEventListener('message', handleMessage);
          const error = new Error(event.data.error || 'GitHub authentication failed');
          setError(error.message);
          onError(error);
        }
      };

      window.addEventListener('message', handleMessage);

      // Clean up listener if popup is closed without completing auth
      const checkPopupClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkPopupClosed);
          window.removeEventListener('message', handleMessage);
        }
      }, 1000);

    } catch (err: any) {
      console.error('GitHub sign in error:', err);
      setError(err.message || 'GitHub sign in failed');
      onError(err);
    }
  }, [isLoaded, clientId, redirectUri, scopes, onSuccess, onError]);

  return {
    isLoaded,
    signIn,
    error,
  };
};

export default useGitHubAuth;
